import ReactECharts from "echarts-for-react";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { formatMarkPoint, formatPercentage } from "../../utilities/helpers.js";
import { getYAxisIndex } from "./Helpers.js";

const BarChart = React.memo((props) => {
  const {
    // Bar layout
    barRadius = [0, 0, 0, 0],
    barWidth = undefined,
    chartheight,
    spaceBottom = "15%",
    spaceLeft = "4%",
    spaceRight = "2%",
    spaceTop = "10%",
    isCustomChartHeight,
    isChartHeight,
    customObjectHeightLength = false,

    // Title & labels
    BarChartTitle = "",
    titlePosition = "left",
    labelConfig = {},
    labelTextColor = "#ff8587",
    showlabelBar = false,
    showLabelInside,
    showSpecificLabel = false,

    // Color & series config
    color = [],
    stackbar = false,
    Stack,
    singleBar = false,
    multipleMapping = false,
    sorting = "descending",
    withOutCustomSeries = false,

    // Axis configuration
    xAxisData = [],
    xAxisTruncateLabel = false,
    xField = "",
    xnamegap = 0,
    yField = "",
    ynamegap = 0,
    yAxisData = [],
    formaterYaxis = null,
    otherYAxisProperty,
    isCustomXAxis = false,
    withOutCustomYAxis = false,
    isDecimalYAxis = true,

    // Scroll and zoom
    isScroll = false,
    isScrollEndLimit = undefined,
    isScrollStartLimit = undefined,
    scrollBarHeight = 15,
    scrollBarBottomAlign = 15,
    hideScroll = false,
    otherZoomProperty = { xAxisIndex: 0 },
    customZoom = undefined,

    // Data
    data,
    nameList = [],
    sideBarChart,
    absoluteData = false,

    // Legend configuration
    customlegend,
    legend = [],
    legendDataArray,
    legendSelection,
    legendPosition,
    legendEvent = true,
    showLegend = true,

    // Mark point / annotations
    markPointData = [],
    enableMarkPoint,
    isMarkPoint,
    CustomMarkpoint,
    getMarkPoints,

    // Scatter plot
    showScatter = false,
    scatterData = {},

    // Events
    onChartClick = null,
    onSprintClick = null,

    // Tooltip
    toolTipConfig,

    // Effects
    enableCubeEffect = false,
    otherLegendProperty,
    otherOptionProperties = false,
    customGridProperties = false,
    otherGridProperties = null,
  } = props;

  let maxValue = Number.NEGATIVE_INFINITY;

  //Sorting logic based on prop
  let sortedData = data;
  let sortedLabels = xAxisData;
  if (sorting !== "none") {
    const combined = data.map((value, index) => ({
      label: xAxisData[index],
      value,
    }));

    const sorted =
      sorting === "ascending"
        ? combined.sort((a, b) => a.value - b.value)
        : combined.sort((a, b) => b.value - a.value);

    sortedData = sorted.map((item) => item.value);
    sortedLabels = sorted.map((item) => item.label);
  }

  const getValue = sortedData?.map((item) => item.data);
  for (const row of getValue) {
    if (row) {
      const maxInRow = Math.max(...row);
      if (maxInRow > maxValue) {
        maxValue = maxInRow;
      }
    }
  }

  ////////////////

  const maxiMumObejectLength = Math.max(
    ...sortedLabels.map((label) => label?.length)
  );
  const YaxisTurncate = function (value) {
    return value.length > 4 ? value.substring(0, 4) + "..." : value;
  };
  //
  const formatYAxis = (value) => {
    if (value >= 1_000_000_000_000) {
      return `${value / 1_000_000_000_000}T`;
    } // Convert to T (trillions)
    if (value >= 1_000_000_000) {
      return `${value / 1_000_000_000}B`;
    } // Convert to B (billions)
    if (value >= 1_000_000) {
      return `${value / 1_000_000}M`;
    } // Convert to M (millions)
    if (value >= 1_000) {
      return `${value / 1_000}K`;
    } // Convert to K (thousands)
    return value;
  };
  const formattedValues =
    Array.isArray(sortedData) && singleBar === true
      ? sortedData.map((value) => Math.floor(formatYAxis(value)))
      : sortedData
          .flatMap((obj) => obj.data)
          .map((value) => Math.floor(formatYAxis(value)));

  const maxDigits = Math.max(
    ...formattedValues.map((value) => value.toString().length)
  );

  const nameGapYAxis = (maxDigits) => {
    let nameGap = 29; // Default value for 2-digit numbers
    if (maxDigits === 3) {
      nameGap = 40;
    } else if (maxDigits === 4) {
      nameGap = 45;
    } else if (maxDigits === 5) {
      nameGap = 48;
    } else if (maxDigits === 6) {
      nameGap = 50;
    } else if (maxDigits === 7) {
      nameGap = 52;
    } else if (maxDigits === 13) {
      nameGap = 77;
    }
    return nameGap;
  };

  const getSeriesData = () => {
    const seriesData = [];

    if (singleBar === true) {
      seriesData.push({
        name: legend[0],
        type: "bar",
        barWidth: barWidth ? barWidth : undefined,
        data: sortedData,
        itemStyle: {
          borderRadius: barRadius ? barRadius : [0, 0, 0, 0],
          color: color ? color[0] : "",
        },
        label: {
          show: showlabelBar,
          position: "top",
          textStyle: {
            color: labelTextColor ? labelTextColor : "#ff8587",
            fontSize: 12,
          },
          ...labelConfig,
        },
      });
    } else if (multipleMapping === true) {
      // Fixed the comparison operator
      for (let i = 0; i < legendDataArray.length; i++) {
        seriesData.push({
          name: legendDataArray[i], // Naming the series by the date (e.g., Apr 24)
          type: "bar",
          data: sortedData?.map((item) => item?.data[i]), // Maps all values for the same date
          barWidth: barWidth ? barWidth : undefined,
          itemStyle: {
            borderRadius: barRadius ? barRadius : [0, 0, 0, 0],
            color: color ? color[i] : "", // Adjusts the color for each series
          },
        });
      }
    } else if (stackbar) {
      for (let i = 0; i < data.length; i++) {
        let markPoints = [];

        if (isMarkPoint && CustomMarkpoint) {
          markPoints = getMarkPoints(data[i], data, markPointData, i);
        } else if (isMarkPoint) {
          // Generate default markPoint from data
          markPoints = data[i].data.map((val, index) => ({
            name: data[i].name,
            coord: [index, val],
            value: val,
          }));
        }

        seriesData.push({
          name: data[i].name,
          type: "bar",
          data: data[i].data,
          ...(stackbar && { stack: data[i].stack }),
          barWidth: barWidth || undefined,
          ...(data[i].label && { label: data[i].label }),
          label: {
            position: "top",
            show: showSpecificLabel ? data[i].showSpecificLabel : false,
            textStyle: {
              fontSize: 12,
              color: "black",
            },
            formatter: function (params) {
              return `${params?.value}`;
            },
          },
          itemStyle: {
            ...(data[i].barWidth && {
              borderColor: data[i].barWidthcolor,
              borderWidth: 4,
              shadowBlur: 2,
              shadowColor: "#ad9fbe",
            }),
            color: stackbar ? data[i].color : color ? color[i] : "",
          },
          ...(markPoints.length > 0 && {
            markPoint: {
              symbol: "pin",
              symbolSize: 40,
              label: {
                show: true,
                color: "#000",
                fontSize: 10,
              },
              data: markPoints,
            },
          }),
        });
      }
    } else if (enableCubeEffect === true) {
      // CUBE EFFECT chart
      data.forEach((seriesGroup, index) => {
        const groupName = seriesGroup.name;
        const groupData = seriesGroup.data;
        const groupColors = color[groupName] || ["#cccccc", "#aaaaaa"];

        // Push a single bar with a linear gradient
        const seriesObj = {
          name: groupName,
          type: "bar",
          data: groupData,
          barWidth: barWidth,
          itemStyle: {
            borderRadius: barRadius ? barRadius : [0, 0, 0, 0],
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: groupColors[0] }, // left half
                { offset: 0.5, color: groupColors[0] },
                { offset: 0.5, color: groupColors[1] }, // right half
                { offset: 1, color: groupColors[1] },
              ],
              global: false,
            },
          },
          z: index + 1,
        };
        if (enableMarkPoint && markPointData?.[index]?.showSpecificLabel) {
          const markPoints = markPointData[index]?.data || [];

          if (markPoints.length > 0) {
            seriesObj.markPoint = {
              symbol: "pin",
              symbolSize: 45,
              label: {
                show: true,
                color: "#000",
                fontSize: 8,
                fontWeight: "bold",
                formatter: (params) => {
                  return markPointData[index].isPercent
                    ? formatPercentage(params?.value)
                    : formatMarkPoint(params?.value);
                },
              },
              data: markPoints,
            };
          }
        }
        seriesData.push(seriesObj);
      });
    } else if (withOutCustomSeries) {
      seriesData.push(...sortedData);
    } else {
      if (absoluteData) {
        seriesData.push(...sortedData);
      } else {
        for (let i = 0; i < legend.length; i++) {
          seriesData.push({
            name: legend[i],
            type: "bar",
            ...(Stack && { stack: sortedData[i]?.stack }),
            data: sortedData[i]?.data,
            barWidth: barWidth,
            itemStyle: {
              borderRadius: barRadius ? barRadius : [0, 0, 0, 0],
              color: color ? color[i] : "",
            },
            ...sortedData[i]?.otherSeriesProperty,
          });
        }
      }
    }

    if (showScatter && scatterData) {
      const scatterPosition =
        seriesData.find((item) => item.type === "bar")?.data || [];
      const data = scatterPosition.map((val, index) => [index, val]);

      seriesData.push({
        name: scatterData.name,
        type: scatterData.type || "scatter",
        data: data,
        yAxisIndex: 0,
        symbol: scatterData.symbol || "circle",
        symbolSize: scatterData.symbolSize || [20, 20],
        symbolOffset: [0, -15],
        label: {
          show: true,
          color: "#000000",
          Position: "inside",
          offset: [0, -3],
          fontSize: 12,
          formatter: function (params) {
            return scatterData.scatterLabelData?.[params.dataIndex] ?? "";
          },
        },
        itemStyle: {
          color: scatterData.color || "#000",
          opacity: 1,
        },
        z: 10000,
      });
    }

    return seriesData;
  };

  const getBarOption = () => {
    let options = {
      title: {
        show: false,
        text: `${BarChartTitle}`,
        x: titlePosition,
        y: 10,
        textStyle: {
          fontSize: 15,
        },
      },
      xAxis: isCustomXAxis
        ? xAxisData
        : {
            type: sideBarChart ? "value" : "category",
            data: sideBarChart ? " " : sortedLabels,
            name: xField,
            nameLocation: "middle",
            nameGap: xnamegap ? xnamegap : 30,
            axisLabel:
              xAxisTruncateLabel === true
                ? {
                    interval: 0,
                    rotate: 0,
                    formatter: (value) =>
                      sortedData?.length < 10
                        ? value.length > 6
                          ? value.substring(0, 6) + "..."
                          : value
                        : value.length > 4
                        ? value.substring(0, 2) + "..."
                        : value,
                  }
                : {},
          },

      yAxis: withOutCustomYAxis
        ? yAxisData
        : {
            type: sideBarChart ? "category" : "value",
            data: sideBarChart ? sortedLabels : " ",
            name: yField,
            show: true,
            nameLocation: "middle",
            nameGap: ynamegap ? ynamegap : nameGapYAxis(maxDigits),
            axisLine: { show: true },
            axisLabel: {
              show: showLabelInside ? false : true,
              formatter: formaterYaxis ? YaxisTurncate : formatYAxis, // Apply formatting
            },
            ...(isDecimalYAxis ? {} : { min: 0, minInterval: 1 }),
            ...(sideBarChart
              ? {}
              : {
                  ...getYAxisIndex(),
                }),
            ...otherYAxisProperty,
          },
      grid: customGridProperties
        ? customGridProperties
        : {
            top: spaceTop,
            left: spaceLeft,
            right: spaceRight,
            bottom: spaceBottom,
            containLabel: true,
            ...otherGridProperties,
          },
      legend: {
        data: legendDataArray,
        ...(customlegend && { selected: legendSelection }),
        icon: "circle",
        show: showLegend,
        type: "scroll",
        orient: "horizontal",
        left: legendPosition ? legendPosition : "left",
        ...otherLegendProperty,
      },
      tooltip: toolTipConfig
        ? { ...toolTipConfig }
        : {
            show: true,
            trigger: "item",
          },
      ...otherOptionProperties,
      series: getSeriesData(),
    };

    if (toolTipConfig) {
      options.tooltip = { ...toolTipConfig };
    } else {
      options.tooltip = {
        show: true,
        trigger: "item",
      };
    }
    const enabledZoom = !hideScroll
      ? showLabelInside
        ? false
        : isScroll
        ? isScroll
        : customObjectHeightLength
        ? maxiMumObejectLength > customObjectHeightLength
        : maxiMumObejectLength > 20
        ? true
        : false
      : false;
    if (enabledZoom) {
      options.dataZoom = customZoom
        ? customZoom
        : [
            {
              type: "slider",
              start: isScrollStartLimit ? isScrollStartLimit : 0,
              end: isScrollEndLimit ? isScrollEndLimit : 30,
              height: scrollBarHeight,
              bottom: scrollBarBottomAlign,
              showDetail: false,
              // backgroundColor: color[0],
              // fillerColor: color[0],
              handleStyle: {
                color: color[0],
              },
              dataBackground: {
                lineStyle: {
                  color: color[0],
                },
                areaStyle: {
                  color: "rgba(255, 0, 0, 0.1)",
                },
              },
              ...otherZoomProperty,
            },
            {
              type: "inside",
              start: 0,
              end: 30,
              ...otherZoomProperty,
            },
          ];
    }
    return options;
  };

  const handleChartClick = (params) => {
    if (params && params.name) {
      const clickedMonthName = params.name;
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const clickedMonthNo = [monthNames.indexOf(clickedMonthName) + 1];
      const clickedMonthNoString = clickedMonthNo.map(String);
      onSprintClick
        ? onSprintClick(clickedMonthName, clickedMonthNoString, params, props)
        : null;
      if (onChartClick) {
        const selectedChartTitle = BarChartTitle ? BarChartTitle : "";
        onChartClick(params, nameList[params.dataIndex], selectedChartTitle);
      }
    }
  };

  let chartInstance = null;
  const handleChartReady = (chart) => {
    chartInstance = chart;
  };
  const handleLegendSelectChanged = (params) => {
    const selectedValues = Object.values(params.selected);
    const allFalse = selectedValues.every((value) => value === false);

    if (allFalse) {
      const YFilledHide = {
        yAxis: {
          type: sideBarChart ? "category" : "value",
          data: sideBarChart ? sortedLabels : " ",
          name: "",
          show: false,
          nameLocation: "middle",
          nameGap: ynamegap ? ynamegap : nameGapYAxis(maxDigits),
          axisLine: { show: false },
          axisLabel: {
            show: false,
            formatter: formatYAxis, // Apply formatting
          },
        },
      };
      if (chartInstance) {
        chartInstance.setOption(YFilledHide);
      }
    } else {
      const YFilledShow = {
        yAxis: {
          type: sideBarChart ? "category" : "value",
          data: sideBarChart ? sortedLabels : " ",
          name: yField,
          show: true,
          nameLocation: "middle",
          nameGap: ynamegap ? ynamegap : nameGapYAxis(maxDigits),
          axisLabel: {
            show: showLabelInside ? false : true,
            formatter: formatYAxis, // Apply formatting
          },
        },
      };

      if (chartInstance) {
        chartInstance.setOption(YFilledShow);
      }
    }
    props.handleLegendSelectChanged && props.handleLegendSelectChanged(params);
  };

  const onEvents = {
    click: handleChartClick,
    ...(legendEvent ? { legendselectchanged: handleLegendSelectChanged } : {}),
  };
  return (
    <div
      style={{
        height:
          chartheight === true
            ? `${isCustomChartHeight ? isCustomChartHeight : "24"}vw`
            : null,
      }}
    >
      <ReactECharts
        option={getBarOption()}
        className={`${isChartHeight}`}
        onEvents={onEvents}
        onChartReady={handleChartReady}
      />
    </div>
  );
});

function areEqual(prevProps, nextProps) {
  if (
    _.isEqual(prevProps.data, nextProps.data) &&
    _.isEqual(prevProps.absoluteData, nextProps.absoluteData) &&
    _.isEqual(prevProps.yAxisData, nextProps.yAxisData) &&
    _.isEqual(prevProps.sortedLabels, nextProps.sortedLabels) &&
    _.isEqual(prevProps.yField, nextProps.yField) &&
    _.isEqual(prevProps.xField, nextProps.xField) &&
    _.isEqual(prevProps.ynamegap, nextProps.ynamegap) &&
    _.isEqual(prevProps.xnamegap, nextProps.xnamegap) &&
    _.isEqual(prevProps.sprintTotalCount, nextProps.sprintTotalCount) &&
    _.isEqual(prevProps.spaceTop, nextProps.spaceTop) &&
    _.isEqual(prevProps.spaceLeft, nextProps.spaceLeft) &&
    _.isEqual(prevProps.spaceRight, nextProps.spaceRight) &&
    _.isEqual(prevProps.color, nextProps.color) &&
    _.isEqual(prevProps.yAxisTextColor, nextProps.yAxisTextColor) &&
    _.isEqual(prevProps.yAxisDataArray, nextProps.yAxisDataArray) &&
    _.isEqual(prevProps.toolTipData, nextProps.toolTipData) &&
    _.isEqual(prevProps.isScrollEndLimit, nextProps.isScrollEndLimit) &&
    _.isEqual(prevProps.isScrollStartLimit, nextProps.isScrollStartLimit) &&
    _.isEqual(prevProps.legend, nextProps.legend) &&
    _.isEqual(prevProps.scatterData, nextProps.scatterData) &&
    _.isEqual(prevProps.spaceBottom, nextProps.spaceBottom) &&
    _.isEqual(prevProps.BarChartTitle, nextProps.BarChartTitle) &&
    _.isEqual(prevProps.titlePosition, nextProps.titlePosition) &&
    _.isEqual(prevProps.showLegend, nextProps.showLegend) &&
    _.isEqual(prevProps.otherZoomProperty, nextProps.otherZoomProperty) &&
    _.isEqual(prevProps.scrollBarHeight, nextProps.scrollBarHeight) &&
    _.isEqual(prevProps.hideScroll, nextProps.hideScroll) &&
    _.isEqual(prevProps.legendPosition, nextProps.legendPosition) &&
    _.isEqual(prevProps.isMarkPoint, nextProps.isMarkPoint) &&
    _.isEqual(prevProps.CustomMarkpoint, nextProps.CustomMarkpoint) &&
    _.isEqual(prevProps.getMarkPoints, nextProps.getMarkPoints) &&
    _.isEqual(prevProps.otherLegendProperty, nextProps.otherLegendProperty) &&
    _.isEqual(prevProps.customZoom, nextProps.customZoom) &&
    _.isEqual(
      prevProps.customObjectHeightLength,
      nextProps.customObjectHeightLength
    ) &&
    _.isEqual(prevProps.showLegend, nextProps.showLegend)
  ) {
    return true;
  } else {
    return false;
  }
}
BarChart.displayName = "BarChart";
BarChart.propTypes = {
  barRadius: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  barWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  BarChartTitle: PropTypes.string,
  titlePosition: PropTypes.string,
  chartheight: PropTypes.bool,
  color: PropTypes.array,
  data: PropTypes.any.isRequired,
  absoluteData: PropTypes.bool,
  legendEvent: PropTypes.bool,
  formaterYaxis: PropTypes.func,
  InsideLeftFormatter: PropTypes.func,
  InsideRightFormatter: PropTypes.func,
  isScroll: PropTypes.bool,
  isScrollEndLimit: PropTypes.number,
  isScrollStartLimit: PropTypes.number,
  labelConfig: PropTypes.object,
  customZoom: PropTypes.object,
  labelTextColor: PropTypes.string,
  legend: PropTypes.array,
  legendDataArray: PropTypes.array,
  markPointData: PropTypes.array,
  multipleMapping: PropTypes.bool,
  nameList: PropTypes.array,
  onChartClick: PropTypes.func,
  onSprintClick: PropTypes.func,
  scatterData: PropTypes.obj,
  scrollBarBottomAlign: PropTypes.number,
  showlabelBar: PropTypes.bool,
  showLabelInside: PropTypes.bool,
  showScatter: PropTypes.bool,
  showSpecificLabel: PropTypes.bool,
  sideBarChart: PropTypes.bool,
  singleBar: PropTypes.bool,
  sorting: PropTypes.oneOf(["ascending", "descending", "none"]),
  spaceBottom: PropTypes.number,
  spaceLeft: PropTypes.number,
  spaceRight: PropTypes.number,
  spaceTop: PropTypes.number,
  stackbar: PropTypes.bool,
  toolTipConfig: PropTypes.object,
  tooltipData: PropTypes.array,
  xAxisData: PropTypes.array,
  xAxisTruncateLabel: PropTypes.bool,
  xField: PropTypes.string,
  xnamegap: PropTypes.number,
  legendPosition: PropTypes.string,
  yField: PropTypes.string,
  ynamegap: PropTypes.number,
  isCustomChartHeight: PropTypes.string,
  customlegend: PropTypes.bool,
  legendSelection: PropTypes.obj,
  handleLegendSelectChanged: PropTypes.func,
  otherZoomProperty: PropTypes.object,
  scrollBarHeight: PropTypes.number,
  hideScroll: PropTypes.bool,
  customObjectHeightLength: PropTypes.any,
  showLegend: PropTypes.bool,
  enableCubeEffect: PropTypes.bool,
  isChartHeight: PropTypes.bool,
  Stack: PropTypes.any,
  withOutCustomSeries: PropTypes.bool,
  isCustomXAxis: PropTypes.bool,
  withOutCustomYAxis: PropTypes.bool,
  isDecimalYAxis: PropTypes.bool,
  enableMarkPoint: PropTypes.bool,
  yAxisData: PropTypes.object,
  otherLegendProperty: PropTypes.object,
  otherYAxisProperty: PropTypes.object,
  isMarkPoint: PropTypes.bool,
  CustomMarkpoint: PropTypes.bool,
  getMarkPoints: PropTypes.func,
  otherOptionProperties: PropTypes.any,
  customGridProperties: PropTypes.any,
  otherGridProperties: PropTypes.any,
};
export default React.memo(BarChart, areEqual);
